import React, { useState, useEffect } from 'react';
import { Brain, Mic, Target, ArrowRight, Menu, X, Users, Globe, Zap, Award, Shield } from 'lucide-react';

const LandingPage = () => {
	const [scrolled, setScrolled] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
	const [isVisible, setIsVisible] = useState(true);
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		organization: '',
		message: ''
	});
	const [submitStatus, setSubmitStatus] = useState('idle');

	const messages = [
		"Which volunteer can handle this crisis?",
		"Does this candidate fit our Kenya office culture?",
		"Which trainer matches this client's personality?",
		"Will this agent connect with this buyer?",
		"Can this soldier transition to this civilian role?",
		"Who on our team fits this new position?"
	];

	useEffect(() => {
		const interval = setInterval(() => {
			setIsVisible(false);
			setTimeout(() => {
				setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
				setIsVisible(true);
			}, 500);
		}, 3000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 20);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const scrollToContact = (e) => {
		e.preventDefault();
		document.getElementById('contact-section').scrollIntoView({ behavior: 'smooth' });
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleSubmit = async () => {
		if (!formData.name || !formData.email || !formData.message) {
			alert('Please fill in all required fields');
			return;
		}

		setSubmitStatus('sending');

		try {
			const response = await fetch('/api/contact', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(formData)
			});

			const data = await response.json();

			if (response.ok) {
				setSubmitStatus('success');
				setFormData({ name: '', email: '', organization: '', message: '' });
				setTimeout(() => {
					setSubmitStatus('idle');
				}, 3000);
			} else {
				throw new Error(data.error || 'Failed to send');
			}
		} catch (error) {
			console.error('Error sending message:', error);
			setSubmitStatus('error');
			setTimeout(() => {
				setSubmitStatus('idle');
			}, 3000);
		}
	};

	return (
		<div className="min-h-screen">
			{/* Header */}
			<header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white bg-opacity-10 backdrop-blur-lg shadow-lg' : ''}`}>
				<div className="container mx-auto flex items-center justify-between p-4">
					<div className="flex items-center">
						<img 
							src="/images/mindaera_logo.png" 
							alt="Mindaera" 
							className="h-8 sm:h-10 w-auto brightness-0 invert"
						/>
					</div>
					
					{/* Desktop Menu */}
					<div className="hidden md:flex items-center space-x-6">
						<a 
							href="#contact-section"
							onClick={scrollToContact}
							className="bg-white bg-opacity-20 backdrop-blur-lg text-white hover:bg-opacity-30 px-4 py-2 rounded-full flex items-center text-sm font-medium transition-all border border-white border-opacity-20"
						>
							Let's Talk
						</a>
					</div>
					
					{/* Mobile Menu Button */}
					<button 
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
						className="md:hidden text-white p-2"
					>
						{mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
					</button>
				</div>
				
				{/* Mobile Menu Dropdown */}
				{mobileMenuOpen && (
					<div className="md:hidden bg-white bg-opacity-10 backdrop-blur-lg border-t border-white border-opacity-20">
						<div className="container mx-auto p-4 space-y-4">
							<a 
								href="#contact-section"
								onClick={(e) => {
									scrollToContact(e);
									setMobileMenuOpen(false);
								}}
								className="block bg-purple-500 bg-opacity-30 backdrop-blur-lg text-white hover:bg-opacity-40 px-4 py-3 rounded-full text-center font-medium transition-all border border-purple-300 border-opacity-30"
							>
								Let's Talk
							</a>
						</div>
					</div>
				)}
			</header>

			{/* Hero Section */}
			<section className="bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 text-white pt-32 pb-20 px-4 relative overflow-hidden">
				<div className="absolute inset-0 overflow-hidden">
					<div className="absolute w-64 sm:w-96 h-64 sm:h-96 bg-purple-500 rounded-full opacity-30 sm:opacity-20 blur-3xl -top-32 sm:-top-48 -left-32 sm:-left-48 animate-pulse"></div>
					<div className="absolute w-64 sm:w-96 h-64 sm:h-96 bg-purple-400 rounded-full opacity-30 sm:opacity-20 blur-3xl -bottom-32 sm:-bottom-48 -right-32 sm:-right-48 animate-pulse" style={{animationDelay: '2s'}}></div>
				</div>

				<div className="container mx-auto max-w-6xl relative z-10">
					<div className="text-center mb-16">
						<h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 animate-fade-in leading-tight" style={{opacity: 0}}>
							Ai-Driven Precision Psychology for Smarter Business
						</h1>
						<p className="text-lg sm:text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-4 animate-fade-in" style={{opacity: 0, animationDelay: '0.2s'}}>
							Match people to people, roles, and situations based on deep psychological compatibility—not surface-level traits.
						</p>
						<p className="text-base sm:text-lg text-blue-200 max-w-2xl mx-auto animate-fade-in" style={{opacity: 0, animationDelay: '0.3s'}}>
							For organizations that need the right match, not just any match.
						</p>
					</div>

					<div className="grid md:grid-cols-2 gap-12 items-center">
						<div className="animate-fade-in" style={{opacity: 0, animationDelay: '0.4s'}}>
							<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white border-opacity-20">
								<div className="flex items-center mb-6">
									<div className="w-14 sm:w-16 h-14 sm:h-16 bg-gradient-to-br from-purple-400 to-blue-400 rounded-2xl flex items-center justify-center mr-4">
										<Brain className="text-white" size={28} />
									</div>
									<div>
										<h2 className="text-xl sm:text-2xl font-bold">How It Works</h2>
										<p className="text-purple-200 sm:text-blue-200">Psychology reveals compatibility</p>
									</div>
								</div>
								<p className="text-base sm:text-lg mb-6 leading-relaxed">
									Conversation builds psychological profiles. Our platform analyzes character, motivation, and temperament—then matches based on true compatibility, not keywords or checkboxes.
								</p>
								
								<a 
									href="#contact-section"
									onClick={scrollToContact}
									className="inline-flex items-center bg-purple-500 bg-opacity-25 sm:bg-white sm:bg-opacity-20 backdrop-blur-lg px-6 py-3 rounded-full font-semibold hover:bg-opacity-35 sm:hover:bg-opacity-30 transition-all transform hover:scale-105 border border-purple-300 sm:border-white border-opacity-40 sm:border-opacity-20 cursor-pointer"
								>
									Explore Your Use Case
									<ArrowRight className="ml-2" size={20} />
								</a>
							</div>
						</div>

						<div className="animate-fade-in" style={{opacity: 0, animationDelay: '0.6s'}}>
							<div className="relative">
								<div className="bg-white bg-opacity-5 backdrop-blur-lg rounded-3xl p-6 sm:p-8 border border-white border-opacity-10">
									<div className="flex justify-center items-center space-x-4 sm:space-x-8">
										<div className="flex items-center space-x-1">
											{[1,2,3,4,3,2,1].map((height, i) => (
												<div 
													key={i}
													className="w-1 bg-purple-400 rounded-full animate-pulse"
													style={{
														height: `${height * 10}px`,
														animationDelay: `${i * 0.1}s`
													}}
												></div>
											))}
										</div>
										<div className="w-16 sm:w-20 h-16 sm:h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center animate-pulse shadow-2xl">
											<Mic className="text-white" size={28} />
										</div>
										<div className="flex items-center space-x-1">
											{[1,2,3,4,3,2,1].map((height, i) => (
												<div 
													key={i}
													className="w-1 bg-blue-400 rounded-full animate-pulse"
													style={{
														height: `${height * 10}px`,
														animationDelay: `${(7-i) * 0.1}s`
													}}
												></div>
											))}
										</div>
									</div>
									<p 
										className={`text-center mt-6 text-purple-200 sm:text-blue-200 text-sm sm:text-base italic transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
									>
										"{messages[currentMessageIndex]}"
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Problem Section */}
			<section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800">
				<div className="container mx-auto max-w-5xl">
					<h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-12">Why Traditional Matching Fails</h2>
					
					<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 sm:p-10 mb-12 border border-white border-opacity-20">
						<p className="text-white text-lg sm:text-xl leading-relaxed mb-6">
							Resumes show credentials, not character. Personality tests capture surface traits, not psychological depth. Interviews reveal polish, not authentic compatibility.
						</p>
						<p className="text-white text-lg sm:text-xl leading-relaxed">
							The result? <span className="font-bold text-purple-400">Wrong matches everywhere</span>—volunteers deployed to situations they can't handle, hires who don't fit company culture, clients paired with incompatible professionals.
						</p>
					</div>
					
					<div className="grid md:grid-cols-3 gap-8 mb-12">
						<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center border border-white border-opacity-20">
							<p className="text-4xl sm:text-5xl font-bold text-purple-400 mb-4">46%</p>
							<p className="text-white text-sm sm:text-base">of new hires fail within 18 months</p>
							<p className="text-purple-300 text-xs sm:text-sm mt-2">Leadership IQ</p>
						</div>
						<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center border border-white border-opacity-20">
							<p className="text-4xl sm:text-5xl font-bold text-blue-400 mb-4">89%</p>
							<p className="text-white text-sm sm:text-base">fail due to poor fit, not lack of skills</p>
							<p className="text-blue-300 text-xs sm:text-sm mt-2">Leadership IQ</p>
						</div>
						<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center border border-white border-opacity-20">
							<p className="text-4xl sm:text-5xl font-bold text-green-400 mb-4">$14,900</p>
							<p className="text-white text-sm sm:text-base">average cost per bad hire</p>
							<p className="text-green-300 text-xs sm:text-sm mt-2">CareerBuilder</p>
						</div>
					</div>

					<div className="text-center">
						<p className="text-xl sm:text-2xl text-gray-300 px-4">
							Surface-level matching creates expensive mismatches. Psychological compatibility is what actually matters.
						</p>
					</div>
				</div>
			</section>

			{/* How It Works Section */}
			<section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-gray-100">
				<div className="container mx-auto max-w-6xl">
					<h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-6">Our Approach</h2>
					<p className="text-lg sm:text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto px-4">
						Conversational AI builds psychological profiles. Our platform matches based on compatibility.
					</p>
					
					<div className="grid md:grid-cols-3 gap-8">
						<div className="group hover:transform hover:scale-105 transition-all duration-300">
							<div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-6 sm:p-8 h-full shadow-lg hover:shadow-2xl transition-shadow">
								<div className="w-14 sm:w-16 h-14 sm:h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform">
									<Mic className="text-white" size={28} />
								</div>
								<h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">1. Conversation Builds Profiles</h3>
								<p className="text-gray-600 leading-relaxed text-sm sm:text-base">
									Natural conversation reveals authentic personality in ways forms and tests cannot. Our conversational AI creates deep psychological profiles through dialogue.
								</p>
							</div>
						</div>
						
						<div className="group hover:transform hover:scale-105 transition-all duration-300">
							<div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8 h-full shadow-lg hover:shadow-2xl transition-shadow">
								<div className="w-14 sm:w-16 h-14 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform">
									<Brain className="text-white" size={28} />
								</div>
								<h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">2. Psychology Analyzes Fit</h3>
								<p className="text-gray-600 leading-relaxed text-sm sm:text-base">
									Built on 35+ years of organizational psychology. We analyze character, motivation, temperament, stress response, and cultural compatibility—the factors that determine real-world success.
								</p>
							</div>
						</div>
						
						<div className="group hover:transform hover:scale-105 transition-all duration-300">
							<div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 sm:p-8 h-full shadow-lg hover:shadow-2xl transition-shadow">
								<div className="w-14 sm:w-16 h-14 sm:h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform">
									<Target className="text-white" size={28} />
								</div>
								<h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4">3. Platform Matches Precisely</h3>
								<p className="text-gray-600 leading-relaxed text-sm sm:text-base">
									Our platform identifies optimal matches based on psychological compatibility. The right volunteer for the crisis. The right candidate for the culture. The right professional for the client.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Applications Section */}
			<section className="py-20 px-4 bg-white">
				<div className="container mx-auto max-w-6xl">
					<h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-800 mb-6">Matching Across Industries</h2>
					<p className="text-lg sm:text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto px-4">
						Any scenario where psychological compatibility determines success—from crisis deployment to client-professional pairing.
					</p>
					
					<div className="grid md:grid-cols-2 gap-8 mb-12">
						{/* Crisis Response */}
						<div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow">
							<div className="flex items-start mb-4">
								<div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
									<Zap className="text-white" size={24} />
								</div>
								<div>
									<h3 className="text-xl font-bold text-gray-800 mb-2">Crisis & Emergency Deployment</h3>
									<p className="text-gray-600 text-sm sm:text-base mb-3">
										Match volunteers to crisis situations based on psychological resilience, stress response, and decision-making under pressure. Deploy the right people to the right emergencies.
									</p>
								</div>
							</div>
							<div className="flex items-center text-red-600 text-sm font-semibold">
								<Shield className="mr-2" size={16} />
								In partnership with Estonian Red Cross
							</div>
						</div>

						{/* Hiring & Talent */}
						<div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow">
							<div className="flex items-start mb-4">
								<div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
									<Globe className="text-white" size={24} />
								</div>
								<div>
									<h3 className="text-xl font-bold text-gray-800 mb-2">Cross-Cultural Hiring</h3>
									<p className="text-gray-600 text-sm sm:text-base mb-3">
										Match candidates to company culture across countries and continents. Critical for global operations where cultural fit determines retention and performance.
									</p>
								</div>
							</div>
							<p className="text-blue-600 text-sm font-semibold">
								Global mining and multinational enterprises
							</p>
						</div>

						{/* Workforce Programs */}
						<div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow">
							<div className="flex items-start mb-4">
								<div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
									<Award className="text-white" size={24} />
								</div>
								<div>
									<h3 className="text-xl font-bold text-gray-800 mb-2">Workforce Transition & Placement</h3>
									<p className="text-gray-600 text-sm sm:text-base mb-3">
										Match individuals to roles that align with their psychological profile. Essential for career transitions, reintegration programs, and workforce development initiatives.
									</p>
								</div>
							</div>
							<p className="text-green-600 text-sm font-semibold">
								Government programs and rehabilitation services
							</p>
						</div>

						{/* Service Matching */}
						<div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow">
							<div className="flex items-start mb-4">
								<div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
									<Users className="text-white" size={24} />
								</div>
								<div>
									<h3 className="text-xl font-bold text-gray-800 mb-2">Client-Professional Pairing</h3>
									<p className="text-gray-600 text-sm sm:text-base mb-3">
										Match clients with professionals based on personality compatibility, communication style, and relationship dynamics. Better matches mean better outcomes.
									</p>
								</div>
							</div>
							<p className="text-purple-600 text-sm font-semibold">
								Real estate, fitness, coaching, professional services
							</p>
						</div>
					</div>

					<div className="text-center">
						<p className="text-lg text-gray-600 mb-6">
							Different matching challenge? Our AI-Driven Precision Psychology platform adapts to any scenario where compatibility matters.
						</p>
						<a 
							href="#contact-section"
							onClick={scrollToContact}
							className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors cursor-pointer"
						>
							Discuss your matching needs <ArrowRight className="ml-2" size={20} />
						</a>
					</div>
				</div>
			</section>

			{/* Validation Section */}
			<section className="py-20 px-4 bg-gradient-to-br from-green-800 to-blue-800">
				<div className="container mx-auto max-w-6xl">
					<h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-12">Validated By Leading Organizations</h2>
					
					<div className="grid md:grid-cols-2 gap-8 mb-16">
						<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 border border-white border-opacity-20">
							<h3 className="text-2xl font-bold text-white mb-4">Estonian Red Cross Partnership</h3>
							<p className="text-white text-base sm:text-lg leading-relaxed">
								Revolutionizing crisis volunteer deployment. Traditional methods took weeks and often resulted in poor stress-response matches. Our AI-Driven Precision Psychology platform identifies the right volunteers for high-pressure situations based on deep psychological compatibility.
							</p>
						</div>

						<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 sm:p-8 border border-white border-opacity-20">
							<h3 className="text-2xl font-bold text-white mb-4">Proven Track Record</h3>
							<p className="text-white text-base sm:text-lg leading-relaxed mb-4">
								Our team has successfully deployed psychological profiling across government, healthcare, law enforcement, and enterprise—building profiles for over 10,000 individuals in high-stakes environments.
							</p>
							<div className="grid grid-cols-5 gap-4 opacity-70">
								<img src="/images/foreign_ministry_logo.png" alt="Government" className="h-10 object-contain brightness-0 invert" />
								<img src="/images/swedbank-logo.png" alt="Banking" className="h-10 object-contain brightness-0 invert" />
								<img src="/images/east-tallinn-hospital-logo.png" alt="Healthcare" className="h-10 object-contain brightness-0 invert" />
								<img src="/images/estonian-police-logo.png" alt="Law Enforcement" className="h-10 object-contain brightness-0 invert" />
								<img src="/images/ergo-logo.png" alt="Insurance" className="h-10 object-contain brightness-0 invert" />
							</div>
						</div>
					</div>

					<div className="text-center">
						<p className="text-xl sm:text-2xl text-white mb-8">
							Organizations across crisis response, global hiring, workforce development, and professional services are exploring matching applications with us.
						</p>
						<p className="text-lg text-green-300 font-semibold">
							Early adopter partnerships available
						</p>
					</div>
				</div>
			</section>

			{/* Team Section */}
			<section className="py-20 px-4 bg-gray-900">
				<div className="container mx-auto max-w-6xl">
					<h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-6">Built by Psychology and AI Experts</h2>
					<p className="text-lg sm:text-xl text-center text-gray-400 mb-16 max-w-3xl mx-auto px-4">
						Decades of organizational psychology expertise combined with cutting-edge conversational AI. We've built our careers understanding what makes people succeed in different contexts.
					</p>
					
					<div className="grid md:grid-cols-3 gap-8">
						<div className="group relative overflow-hidden rounded-2xl shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-3xl">
							<div 
								className="w-full h-80 sm:h-96 bg-cover bg-center"
								style={{ backgroundImage: `url('/images/david.jpg')` }}
							></div>
							<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10"></div>
							<div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 z-20">
								<h3 className="text-lg sm:text-xl font-bold text-white mb-1">Dr. David Stephens</h3>
								<p className="text-purple-300 font-semibold mb-2 text-sm sm:text-base">Chief Psychology Officer</p>
								<p className="text-gray-300 text-xs sm:text-sm">
									Former Dean of University of the Rockies and Director of Mental Health of Missouri and Colorado. 35+ years in organizational psychology and human compatibility research.
								</p>
							</div>
						</div>
						
						<div className="group relative overflow-hidden rounded-2xl shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-3xl">
							<div 
								className="w-full h-80 sm:h-96 bg-cover bg-center"
								style={{ backgroundImage: `url('/images/erik.jpg')` }}
							></div>
							<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10"></div>
							<div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 z-20">
								<h3 className="text-lg sm:text-xl font-bold text-white mb-1">Erik Rüütel</h3>
								<p className="text-blue-300 font-semibold mb-2 text-sm sm:text-base">Chief Executive Officer</p>
								<p className="text-gray-300 text-xs sm:text-sm">
									Executive Psychological Crisis Advisor of Estonian Internal Security. Former Deputy Governor of Tallinn Prison. Built and managed psychological profiles for 10,000+ individuals in high-stakes environments.
								</p>
							</div>
						</div>
						
						<div className="group relative overflow-hidden rounded-2xl shadow-2xl transform transition-all duration-300 hover:scale-105 hover:shadow-3xl">
							<div 
								className="w-full h-80 sm:h-96 bg-cover bg-center"
								style={{ backgroundImage: `url('/images/youssef.jpg')` }}
							></div>
							<div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent z-10"></div>
							<div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 z-20">
								<h3 className="text-lg sm:text-xl font-bold text-white mb-1">Youssef El Moumni</h3>
								<p className="text-green-300 font-semibold mb-2 text-sm sm:text-base">Chief Technology Officer</p>
								<p className="text-gray-300 text-xs sm:text-sm">
									3x Startup Founder. 2x Patent Holder (StereoVision + Real-Time Video Streaming). AI Pioneer building scalable conversational intelligence for complex psychological profiling.
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Contact/Partnership Section */}
			<section id="contact-section" className="py-20 px-4 bg-gradient-to-br from-purple-900 via-blue-900 to-purple-900">
				<div className="container mx-auto max-w-4xl">
					<div className="text-center mb-12">
						<h2 className="text-3xl sm:text-5xl font-bold text-white mb-6">
							Early Adopter Partnerships
						</h2>
						<p className="text-lg sm:text-xl text-purple-200 max-w-2xl mx-auto px-4">
							Have a matching challenge? Let's discuss how precision psychology can transform your organization.
						</p>
					</div>
					
					<div className="bg-white rounded-3xl p-8 sm:p-12 shadow-2xl">
						<div className="space-y-6">
							<div className="grid md:grid-cols-2 gap-6">
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">
										Your Name *
									</label>
									<input
										type="text"
										name="name"
										value={formData.name}
										onChange={handleInputChange}
										className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
										placeholder="John Smith"
									/>
								</div>
								<div>
									<label className="block text-sm font-semibold text-gray-700 mb-2">
										Email *
									</label>
									<input
										type="email"
										name="email"
										value={formData.email}
										onChange={handleInputChange}
										className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
										placeholder="john@company.com"
									/>
								</div>
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">
									Organization
								</label>
								<input
									type="text"
									name="organization"
									value={formData.organization}
									onChange={handleInputChange}
									className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all"
									placeholder="Your company or organization"
								/>
							</div>
							<div>
								<label className="block text-sm font-semibold text-gray-700 mb-2">
									Tell us about your matching challenge *
								</label>
								<textarea
									name="message"
									value={formData.message}
									onChange={handleInputChange}
									rows="4"
									className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none transition-all resize-none"
									placeholder="What matching problem are you trying to solve? (e.g., crisis volunteer deployment, cross-cultural hiring, client-professional pairing)"
								/>
							</div>
							<button
								onClick={handleSubmit}
								disabled={submitStatus === 'sending'}
								className={`w-full ${
									submitStatus === 'success' ? 'bg-gradient-to-r from-green-600 to-green-600' :
									submitStatus === 'error' ? 'bg-gradient-to-r from-red-600 to-red-600' :
									'bg-gradient-to-r from-purple-600 to-blue-600'
								} text-white font-bold py-4 rounded-xl hover:shadow-lg transition-all text-lg flex items-center justify-center ${
									submitStatus === 'sending' ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'
								}`}
							>
								{submitStatus === 'sending' ? 'Sending...' :
								 submitStatus === 'success' ? 'Sent! ✓' :
								 submitStatus === 'error' ? 'Error - Try Again' :
								 'Start the Conversation'}
								{submitStatus === 'idle' && <ArrowRight className="ml-2" size={20} />}
							</button>
						</div>
						
						<div className="mt-8 pt-8 border-t border-gray-200 text-center">
							<p className="text-sm text-gray-600 mb-3">
								<span className="font-semibold">Early Adopter Benefits:</span>
							</p>
							<div className="grid md:grid-cols-3 gap-4 text-sm text-gray-600">
								<div>• Custom integration support</div>
								<div>• Preferential pricing</div>
								<div>• Shape product roadmap</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Footer */}
			<footer className="bg-black text-gray-400 py-12">
				<div className="container mx-auto px-4">
					<div className="flex flex-col md:flex-row justify-between items-center">
						<div className="flex items-center mb-4 md:mb-0">
							<img 
								src="/images/mindaera_logo.png" 
								alt="Mindaera" 
								className="h-8 w-auto brightness-0 invert opacity-90"
							/>
						</div>
						<div className="text-xs sm:text-sm text-center md:text-right">
							<p>Ai-Driven Precision Psychology for Smarter Business</p>
							<p className="mt-2 text-gray-500">© 2025 Mindaera. All rights reserved.</p>
						</div>
					</div>
				</div>
			</footer>

			<style>{`
				@keyframes fade-in {
					from {
						opacity: 0;
						transform: translateY(20px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				
				.animate-fade-in {
					animation: fade-in 0.8s ease-out forwards;
				}
			`}</style>
		</div>
	);
};

export default LandingPage;